import AttachmentPreview from "@/business/notify/AttachmentPreview";
import { formatDate } from "%/date";
import { Button } from "@/Button";
import DateButton from "@/common/DateButton";
import MessageModal from "@/MessageModal";
import { Dialog } from "@/Dialog";
import ToggleSwitch from "@/common/ToggleSwitch";
import { ValidateSelectList } from "@/SelectList";
import { ValidateSelectListMulti } from "@/SelectListMulti";
import { ValidateTextarea } from "@/Textarea";
import { Controller } from "react-hook-form";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import { useRouter } from "@tanstack/react-router";
import useNotifySendNotify from "^/business/notify/useNotifySendNotify";
import { ValidateInput } from "@/Input";
import {
  groupRecipientOptions,
  expandRecipientValues,
} from "./groupedRecipientOptions";
import { useMemo } from "react";
import {
  canUseNativePhotoPicker,
  openNativePhotoPicker,
} from "%/nativePhotoPicker";
// [문서 첨부 임시 비활성] uploadResource 브릿지는 단건 업로드 계약이라 다중 선택 불가.
//   앱 브릿지가 다중 선택(배열 콜백)을 지원하면 다시 활성화한다.
// import {
//   canUseResourceBridge,
//   uploadResourceViaBridge,
// } from "%/appResourceUpload";
import {
  MAX_NOTICE_ATTACHMENTS,
  NOTICE_ATTACHMENT_LIMIT_MESSAGE,
} from "%/noticeAttachmentPolicy";
// 기본 갤러리 데이터 (API 호출 실패 시 사용)
// const defaultGalleryData: GalleryImage[] = [
//   {
//     ImgID: 1,
//     ImgURL: "https://images.unsplash.com/photo-1543466835-00a7907e9de1",
//   },
//   {
//     ImgID: 2,
//     ImgURL: "https://images.unsplash.com/photo-1548199973-03cce0bbc87b",
//   },
// ];

const SendNotify = () => {
  const router = useRouter();
  const {
    // 폼 관련
    formMethods: {
      handleSubmit,
      control,
      register,
      watch,
      formState: { isValid, errors },
    },

    // 선택 목록 데이터
    selectListData,

    // 날짜 관련
    date,
    selectedDate,
    setSelectedDate,

    // 모달 관련
    calendarModal: { isOpenModal, clickModal, closeModal },
    imageModal: {
      isOpenModal: isOpenImageModal,
      clickModal: clickImageModal,
      closeModal: closeImageModal,
    },

    // 이미지 관련
    selectedImages,
    modalSelectedImages,
    toggleImageSelection,
    removeSelectedImage,
    handleImgModal,
    addBridgeImages,

    // 앨범 관련
    albumData,
    fetchAlbumList,

    // 토글 관련
    // isToggle,
    // setIsToggle,
    isTimeToggle,
    // setIsTimeToggle, // handleTimeToggleChange에서 처리
    handleTimeToggleChange,

    // 다이얼로그 관련
    showSuccessDialog,
    setShowSuccessDialog,

    // 제출 관련
    bindSubmit,
    isSubmitting,
  } = useNotifySendNotify();

  // 사진 첨부는 알림장(SendInform) 탭과 동일한 방식:
  //   - 앱(신버전): 네이티브 통합 사진첩(스마트폰+클라우드, 합산 100개). 업로드 경로는 기본(upload-image,
  //     PHOTO_ALBUM 타입)이라 영상 썸네일 파이프라인도 알림장과 동일하게 탄다.
  //   - 구버전 앱·브라우저: 기존 클라우드 앨범 모달.
  //   공지사항은 수신자가 여럿일 수 있어, 반려견 1마리만 선택된 경우에만 그 펫의 클라우드 탭을 열고
  //   여러 마리면 petSeq 를 넘기지 않아 스마트폰 사진만 고른다.
  const handlePickPhotos = async () => {
    const selectedRecipients = watch("recipient");
    const petSeqs = Array.from(expandRecipientValues(selectedRecipients));
    if (petSeqs.length === 0) {
      alert("먼저 수신자를 선택해주세요.");
      return;
    }
    const remaining = MAX_NOTICE_ATTACHMENTS - selectedImages.length;
    if (remaining <= 0) {
      alert(NOTICE_ATTACHMENT_LIMIT_MESSAGE);
      return;
    }
    if (canUseNativePhotoPicker()) {
      try {
        const picked = await openNativePhotoPicker({
          petSeq: petSeqs.length === 1 ? Number(petSeqs[0]) : undefined,
          multiSelect: remaining,
        });
        if (picked.length) addBridgeImages(picked);
      } catch (err) {
        alert(
          err instanceof Error ? err.message : "사진첩을 여는 데 실패했습니다."
        );
      }
      return;
    }
    // 앨범 목록 가져오기 (기존 웹 모달)
    fetchAlbumList(selectedRecipients);
    clickImageModal();
  };
  // [문서 첨부 임시 비활성] 단건 uploadResource 브릿지 사용분. 앱 다중 선택 지원 후 복구.
  // const handlePickAttachment = async (type: "media" | "document") => {
  //   try {
  //     const r = await uploadResourceViaBridge(
  //       "/api/business/photo/upload-file",
  //       type
  //     );
  //     addBridgeImage(r.fileSeq, r.fileUrl);
  //   } catch (e) {
  //     alert(e instanceof Error ? e.message : "파일 첨부에 실패했습니다.");
  //   }
  // };

  // 같은 보호자의 반려견은 옵션 하나로 묶음 — "초코, 보리 (홍길동)"
  const recipientOptions = useMemo(
    () => groupRecipientOptions(selectListData?.recipients),
    [selectListData?.recipients]
  );

  return (
    <>
      <form className="flex-col flex-auto gap-1 px-5 bg-[#F5F5F5] grid grid-cols-1 animate-fadeIn">
        <div className="mb-5 animate-slideInFromTop">
          <Controller
            name="recipient"
            control={control}
            rules={{ required: "필수 선택입니다." }}
            render={({ field }) => {
              const allValues = recipientOptions.map((option) => option.value);
              const current = Array.isArray(field.value)
                ? field.value
                : field.value
                  ? [field.value]
                  : [];
              const allSelected =
                allValues.length > 0 && current.length >= allValues.length;
              return (
                <>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#333]">
                      수신
                    </span>
                    <button
                      type="button"
                      disabled={allValues.length === 0}
                      onClick={() =>
                        field.onChange(allSelected ? [] : allValues)
                      }
                      className="rounded-full border border-[#FF9934] px-3 py-1 text-xs font-semibold text-[#FF9934] disabled:opacity-40"
                    >
                      {allSelected ? "전체 해제" : "전체 선택"}
                    </button>
                  </div>
                  <ValidateSelectListMulti
                    onChange={(selectedValues) => {
                      field.onChange(selectedValues);
                    }}
                    placeholder="반려견 이름 선택 (다중선택 가능)"
                    options={recipientOptions}
                    value={current}
                    errorMessage={errors?.recipient?.message}
                  />
                </>
              );
            }}
          />
          <div className="my-4">
            <ValidateInput
              className="w-full"
              label="제목"
              placeholder="제목을 입력하세요."
              errorMessage={errors?.notiTitle?.message}
              {...register("notiTitle", {
                required: { value: true, message: "제목을 입력하세요" },
              })}
            />
          </div>
          <div
            className="my-5 animate-slideInFromTop"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="flex justify-between items-center mb-1">
              <p className="mb-2 text-sm font-medium">발송시간</p>
              <ToggleSwitch
                checked={isTimeToggle}
                onChange={handleTimeToggleChange}
                // disabled={isUpdating}
              />
            </div>
            {isTimeToggle && (
              <div className="flex gap-1 items-center mb-1">
                <DateButton
                  value={selectedDate ? formatDate(selectedDate) : date}
                  onClick={() => clickModal()}
                />
                <Controller
                  name="hour"
                  control={control}
                  rules={{ required: "시간을 선택하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      isDefaultValue={true}
                      placeholder="시"
                      isSearchable={false}
                      options={Array.from({ length: 24 }, (_, i) => ({
                        value: (i + 1).toString().padStart(2, "0"),
                        label: (i + 1).toString().padStart(2, "0"),
                      }))}
                      boxHeight={20}
                      className="h-10 text-lg text-center"
                      value={
                        field.value
                          ? {
                              value: field.value,
                              label: field.value,
                            }
                          : null
                      }
                    />
                  )}
                />
                <span className="text-[12px]">시</span>
                <Controller
                  name="minute"
                  control={control}
                  rules={{ required: "분을 선택하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      isDefaultValue={true}
                      placeholder="분"
                      isSearchable={false}
                      options={Array.from({ length: 6 }, (_, i) => ({
                        value: (i * 10).toString().padStart(2, "0"),
                        label: (i * 10).toString().padStart(2, "0"),
                      }))}
                      boxHeight={20}
                      className="h-10 text-lg text-center"
                      value={
                        field.value
                          ? {
                              value: field.value,
                              label: field.value,
                            }
                          : null
                      }
                    />
                  )}
                />
                <span className="text-[12px]">분</span>
              </div>
            )}
          </div>
          <div
            className="my-5 animate-slideInFromTop"
            style={{ animationDelay: "0.2s" }}
          >
            <Controller
              name="content"
              control={control}
              rules={{ required: "필수 선택입니다." }}
              render={({ field }) => (
                <ValidateTextarea
                  {...field}
                  className="w-full h-100"
                  label="내용"
                  rows={10}
                />
              )}
            />
          </div>

          {/* 사진 등록 */}
          <div
            className="my-5 animate-slideInFromTop"
            style={{ animationDelay: "0.3s" }}
          >
            <p className="mb-2 text-sm font-medium">
              사진등록 ({selectedImages.length}/{MAX_NOTICE_ATTACHMENTS})
            </p>
            <div className="flex justify-between gap-2">
              {/* 알림장 탭과 동일: 앱은 네이티브 통합 사진첩(다중), 그 외는 클라우드 앨범 모달 */}
              <button
                type="button"
                className="w-full min-h-[36px] bg-white rounded-md border border-gray-400 transition-all duration-300 hover:bg-gray-50 hover:border-gray-500 transform hover:scale-[1.02]"
                onClick={(e) => {
                  e.preventDefault();
                  handlePickPhotos();
                }}
              >
                <p>사진 불러오기</p>
              </button>
              {/* [문서 첨부 임시 비활성] 단건 브릿지라 다중 선택 불가 — 앱 지원 후 복구
              <button
                type="button"
                className="w-full min-h-[36px] bg-white rounded-md border border-gray-400 transition-all duration-300 hover:bg-gray-50 hover:border-gray-500 transform hover:scale-[1.02]"
                onClick={(e) => {
                  e.preventDefault();
                  handlePickAttachment("document");
                }}
              >
                <p>문서</p>
              </button>
              */}
            </div>
            {/* 선택된 사진 미리보기 */}
            <div className="flex flex-col gap-2 overflow-y-auto max-h-[500px] mt-3">
              <div className="grid grid-cols-4 gap-1 sm:grid-cols-4">
                {selectedImages.map((data) => {
                  return (
                    <div
                      key={data.ImgID}
                      className="relative cursor-pointer aspect-square"
                    >
                      <AttachmentPreview
                        fileSeq={data.ImgID}
                        originalUrl={data.ImgURL}
                        thumbnailUrl={data.thumbnailUrl}
                        thumbnailStatus={data.thumbnailStatus}
                      />
                      {/* X(삭제) 버튼 */}
                      <button
                        type="button"
                        className="flex absolute top-1 right-1 z-10 justify-center items-center w-4 h-4 text-xs bg-white bg-opacity-60 rounded-full hover:bg-opacity-90"
                        onClick={() => removeSelectedImage(data)}
                        style={{ lineHeight: 1 }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M4 4L12 12"
                            stroke="gray"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <path
                            d="M12 4L4 12"
                            stroke="gray"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </form>
      <div className="fixed left-1/2 -translate-x-1/2 w-full max-w-[480px] bottom-0 md:bottom-6 md:rounded-b-3xl z-40 px-4 pt-3 pb-[calc(0.75rem+var(--nav-bottom,0px)+env(safe-area-inset-bottom,0px))] bg-white border-t border-gray-200 shadow-lg flex gap-2">
        <Button
          className="w-full bg-transparent"
          style={{ pointerEvents: "auto" }}
          onClick={handleSubmit((data: any) => bindSubmit(data, true))}
          disabled={!isValid || isSubmitting}
        >
          <div className="py-3 mx-auto w-full font-semibold text-center text-yellow-500 bg-white rounded-full border border-yellow text-[16px] text-yellow transition-all duration-300 hover:bg-yellow-50 hover:border-yellow-400 transform hover:scale-[1.02]">
            저장하기
          </div>
        </Button>
        <Button
          className="w-full bg-transparent"
          style={{ pointerEvents: "auto" }}
          onClick={handleSubmit((data: any) => bindSubmit(data, false))}
          disabled={!isValid || isSubmitting}
        >
          <div className="py-3 mx-auto w-full font-semibold text-center text-white bg-orange-400 rounded-full text-[16px] transition-all duration-300 hover:bg-orange-500 transform hover:scale-[1.02]">
            {isTimeToggle ? "예약 발송" : "즉시 발송"}
          </div>
        </Button>
      </div>
      {/* 모달 영역 */}
      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenModal}
        onRequestClose={closeModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          disablePast={true}
          dayOnClick={(day) => {
            setSelectedDate(day);
          }}
          selectedDay={selectedDate}
          schedules={{}}
        />
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={closeModal}
          >
            확인
          </button>
        </div>
      </MessageModal>

      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenImageModal}
        onRequestClose={closeImageModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex flex-col gap-2 overflow-y-auto h-[500px]">
          <div className="grid grid-cols-4 gap-1 sm:grid-cols-4">
            {(albumData.length > 0 ? albumData : []).map((data) => {
              return (
                <div
                  key={data.ImgID}
                  className={`relative cursor-pointer aspect-square border-2 rounded-lg ${
                    modalSelectedImages?.find(
                      (item) => item.ImgID === data.ImgID,
                    )
                      ? " border-orange-500"
                      : "border-white bg-white"
                  }`}
                  onClick={() => toggleImageSelection(data)}
                >
                  <AttachmentPreview
                    fileSeq={data.ImgID}
                    originalUrl={data.ImgURL}
                    thumbnailUrl={data.thumbnailUrl}
                    thumbnailStatus={data.thumbnailStatus}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeImageModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={handleImgModal}
          >
            선택
          </button>
        </div>
      </MessageModal>

      {/* 성공 다이얼로그 */}
      <Dialog
        visible={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">공지사항 발송 완료</h3>
          <p className="mb-4 text-gray-600">
            공지사항이 성공적으로 발송되었습니다.
          </p>
          <button
            onClick={() => {
              setShowSuccessDialog(false);
              router.navigate({ to: "/business/layout/notify" });
            }}
            className="px-4 py-2 text-white bg-orange-400 rounded-lg hover:bg-orange-500"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default SendNotify;
